import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';

const planBadgeColors = {
  FREE: { bg: '#F1F5F9', color: '#64748B' },
  STARTER: { bg: '#EFF6FF', color: '#2563EB' },
  PRO: { bg: '#F5F3FF', color: '#7C3AED' },
};

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [accountPlan, setAccountPlan] = useState(null);

  useEffect(() => {
    api.get('/account').then(res => setAccountPlan(res.data.plan)).catch(() => {});
  }, []);
  const [theme, setTheme] = useState(localStorage.getItem('cardlockr_theme') || 'light');

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('cardlockr_theme', theme);
  }, [theme]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        onToggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onToggle]);

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-brand">
        <div className="brand-icon">
          <i className="fas fa-wallet"></i>
        </div>
        <h2>CardLockr</h2>
        <button className="sidebar-toggle" onClick={onToggle} title="Ctrl+B">
          <i className={`fas fa-chevron-${collapsed ? 'right' : 'left'}`}></i>
        </button>
      </div>

      <nav className="sidebar-nav">
        <div className="sidebar-section">{t.sidebar.principal}</div>
        <NavLink to="/dashboard" end className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <i className="fas fa-chart-pie"></i><span>{t.sidebar.dashboard}</span>
        </NavLink>
        <NavLink to="/dashboard/gift-cards" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <i className="fas fa-gift"></i><span>{t.sidebar.giftCards}</span>
        </NavLink>
        <NavLink to="/dashboard/suppliers" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <i className="fas fa-store"></i><span>{t.sidebar.suppliers}</span>
        </NavLink>
        <NavLink to="/dashboard/transactions" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <i className="fas fa-exchange-alt"></i><span>{t.sidebar.transactions}</span>
        </NavLink>
        <NavLink to="/dashboard/reports" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <i className="fas fa-file-alt"></i><span>{t.sidebar.reports}</span>
        </NavLink>

        <div className="sidebar-section">{t.sidebar.admin}</div>
        {user?.role === 'ADMIN' && (
          <NavLink to="/dashboard/admin" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <i className="fas fa-shield-alt"></i><span>{t.sidebar.adminPanel}</span>
          </NavLink>
        )}
        <NavLink to="/dashboard/users" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <i className="fas fa-users"></i><span>{t.sidebar.users}</span>
        </NavLink>
        <NavLink to="/dashboard/settings" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <i className="fas fa-cog"></i>
          <span>{t.sidebar.settings}</span>
          {accountPlan && !collapsed && (
            <span style={{
              marginLeft: 'auto',
              background: planBadgeColors[accountPlan]?.bg || '#F1F5F9',
              color: planBadgeColors[accountPlan]?.color || '#64748B',
              fontSize: '0.6rem',
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 4,
              letterSpacing: '0.02em',
            }}>{accountPlan}</span>
          )}
        </NavLink>
        <NavLink to="/dashboard/help" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <i className="fas fa-circle-question"></i>
          <span>{t.sidebar.help}</span>
        </NavLink>
        {/* DISABLED: integrations link (security review pending) */}
        <a href="/blog" target="_blank" rel="noopener noreferrer" className="sidebar-link">
          <i className="fas fa-blog"></i>
          <span>{t.sidebar.blog || 'Blog'}</span>
        </a>
      </nav>

      <div className="sidebar-footer">
        <div className="theme-toggle">
          <button className="theme-toggle-btn" onClick={() => setTheme(th => th === 'light' ? 'dark' : 'light')} title="Toggle theme">
            <i className={`fas fa-${theme === 'light' ? 'moon' : 'sun'}`}></i>
          </button>
          <span className="theme-toggle-label">{theme === 'light' ? t.sidebar.darkMode : t.sidebar.lightMode}</span>
        </div>
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{initials}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.name}</div>
            <div className="sidebar-user-role">{user?.role === 'ADMIN' ? t.sidebar.administrator : t.sidebar.operator}</div>
          </div>
          <button className="btn-logout" onClick={logout} title={t.sidebar.logout}>
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
